#![feature(prelude_import)]
//! The client half of the page's islands: a `#![no_std]` crate whose whole
//! content is the island modules the server also compiles.
//!
//! There is no code of its own here on purpose. An island's client half is
//! generated from the same view the server rendered, and the entry point the
//! loader calls is the `#[no_mangle]` function that generation produces, which
//! is both this crate's export and the root its dead code elimination keeps.

// What is NOT here, and used to be: `#![allow(internal_features)]` and
// `#![feature(panic_internals, trivial_clone, derive_clone_copy_internals)]`.
//
// A crate that uses the view macros is macro expanded first and the EXPANDED
// TEXT is what gets compiled, so anything a macro lowers to has to be spelled in
// a source file rather than only understood by the compiler that wrote it.
// Three of the library's own lowerings are internal and need a gate for that
// reason alone. None of it is about the islands: the same code compiled in ONE
// pass needs no gate at all.
//
// So the gates are a property of the two-pass arrangement, which is jsc-build's
// and not this crate's, and jsc-build injects them into the expand step. See
// `jsc-build/src/toolchain.rs`, `EXPANSION_GATES`, which also says which
// lowering wants which gate.
#![no_std]
#![no_main]








#![allow(internal_features)]
#![feature(panic_internals, trivial_clone, derive_clone_copy_internals)]
extern crate core;
#[prelude_import]
#[allow(unused_imports)]
use core::prelude::rust_2021::*;
#[panic_handler]
fn panic(_: &core::panic::PanicInfo) -> ! { loop {} }
#[path = "../island/bench.rs"]
mod bench {
    //! The benchmark island: the whole of the js-framework-benchmark app as one
    //! island, so that the harness measures the idiomatic path rather than a
    //! hand-written one.
    //!
    //! Like every island this file is a module of two crates. The server crate reads
    //! it as ordinary Topcoat and renders the opening page as HTML with the hydration
    //! keys in it; the client crate reads it with `topcoat_client` set and the rustc
    //! backend compiles the same view to JavaScript. What is new here is not a
    //! technique but a MEASUREMENT: this is the framework's own way of writing a list
    //! of rows, put in front of a benchmark that has published numbers for everybody
    //! else's.
    //!
    //! # One signal, and what that costs
    //!
    //! There is exactly one signal, `version`, and it holds a number nobody reads for
    //! its value. Every operation writes the rows into the host's store and then bumps
    //! it, and the table's loop reads it, so a bump is what rebuilds the table. That
    //! is the honest shape of a reactive `for` here: the loop's iterated expression is
    //! what the runtime subscribes to, so ANY write rebuilds EVERY row -- a thousand
    //! clones and a couple of thousand mutations for a single selection.
    //!
    //! A framework that beats this does one of two things: it keys the rows (so a
    //! reorder moves nodes), or it puts the changing part of a row behind a hole of
    //! the row's own template (so a change patches one text node). Both are available
    //! here and neither is used, because what this entry is for is the number the
    //! straightforward version gets. The recycling version lives outside demo-app, as
    //! the benchmark's second entry.
    //!
    //! # Where the rows live
    //!
    //! In the host, as a list of `{ id, label }` objects reached through
    //! [`island-rt`](../src/island-rt.mjs). The client crate is `#![no_std]` with no
    //! heap and no mutable statics, so a list that survives from one click to the next
    //! is not a thing it can declare, and a label is a string that has to be built
    //! from three words, which needs the heap it does not have. So the store is
    //! JavaScript and every DECISION is Rust: which words a label is made of, which
    //! rows a bang lands on, which two rows swap, when the swap is legal at all.
    //!
    //! # Why the rows are read one at a time
    //!
    //! [`Rows`] is an iterator over the store rather than a collection: the rows are
    //! the host's and there is nowhere in this crate to copy them to. It hands the
    //! loop a [`RowView`] per row, which is four borrowed values and no allocation.
    //!
    //! # Why the handlers are per row
    //!
    //! The other grid-shaped islands here (Life, Minesweeper) use one handler on the
    //! container and `value=(index)` on each cell, which is cheaper. The benchmark's
    //! row contract fixes the markup: the label is an `<a>` and so is the delete
    //! control, and an `<a>` has no `value` for `event.target.value` to read. So this
    //! island uses the other shape that `examples/dom-tests/23_for_row_handlers.rs`
    //! pins: a closure per row, capturing the row's index. Two per row, rebuilt on
    //! every render, which is part of what is being measured.
    //!
    //! The index is bound with a `let` inside the loop rather than read as
    //! `row.index` inside the handler. A `$( ... )` value is compiled by the server's
    //! expression language as well as by the client compiler, and that language
    //! captures a free identifier WHOLE: `row.index` would ask it to serialize a
    //! `RowView`, which is not one of its types. An `f64` is.
    /// The adjectives a label may start with.
    ///
    /// The three tables and the way a word is picked from them are the benchmark's,
    /// copied exactly: a different table or a different draw would be a different
    /// amount of text in the DOM, which is a thing the benchmark measures.
    const ADJECTIVES: [&str; 25] =
        ["pretty", "large", "big", "small", "tall", "short", "long",
                "handsome", "plain", "quaint", "clean", "elegant", "easy",
                "angry", "crazy", "helpful", "mushy", "odd", "unsightly",
                "adorable", "important", "inexpensive", "cheap", "expensive",
                "fancy"];
    /// The colours. Eleven entries with "brown" twice, which is the benchmark's own
    /// table and not a mistake being carried over: the duplicate is what makes brown
    /// twice as likely, and every published entry has it.
    const COLOURS: [&str; 11] =
        ["red", "yellow", "blue", "green", "pink", "brown", "purple", "brown",
                "white", "black", "orange"];
    /// The nouns a label ends with.
    const NOUNS: [&str; 13] =
        ["table", "chair", "house", "bbq", "desk", "car", "pony", "cookie",
                "sandwich", "burger", "pizza", "mouse", "keyboard"];
    /// How many rows the create and append buttons make.
    const BATCH: i32 = 1_000;
    /// How many the big create button makes.
    const BIG_BATCH: i32 = 10_000;
    /// The gap between the rows an update touches: every tenth from the first.
    const EVERY: f64 = 10.0;
    /// The two rows a swap exchanges, and the length that makes the exchange legal.
    ///
    /// The benchmark's numbers, and the guard is part of them: with 998 rows or fewer
    /// the swap does nothing at all rather than swapping something else.
    const SWAP_LOW: f64 = 1.0;
    const SWAP_HIGH: f64 = 998.0;
    /// One row, as the table's loop draws it.
    ///
    /// Four values read out of the host's store. `index` is the row's position rather
    /// than its id, because the two handlers act by position and the store is a list.
    /// `class` is derived by the store from the one selected id, so a row carries no
    /// flag of its own and a selection changes one row's class without touching any
    /// other row's data.
    struct RowView {
        index: f64,
        id: f64,
        label: &'static str,
        class: &'static str,
    }
    #[automatically_derived]
    #[doc(hidden)]
    unsafe impl ::core::clone::TrivialClone for RowView { }
    #[automatically_derived]
    impl ::core::clone::Clone for RowView {
        #[inline]
        fn clone(&self) -> RowView {
            let _: ::core::clone::AssertParamIsClone<f64>;
            let _: ::core::clone::AssertParamIsClone<&'static str>;
            let _: ::core::clone::AssertParamIsClone<&'static str>;
            *self
        }
    }
    #[automatically_derived]
    impl ::core::marker::Copy for RowView { }
    /// The rows to draw, read straight out of the host's store.
    ///
    /// `version` is read for the subscription and not for the number: reading it is
    /// what makes the table rebuild when an operation bumps it, and the store is where
    /// the rows come from.
    fn rows(_version: f64) -> Rows {
        Rows { at: 0.0, len: unsafe { bench_len() } }
    }
    /// The rows of the store, one at a time.
    ///
    /// An iterator rather than a collection: the rows are the host's and this crate
    /// has no heap to copy them into.
    struct Rows {
        at: f64,
        len: f64,
    }
    impl Iterator for Rows {
        type Item = RowView;
        fn next(&mut self) -> Option<RowView> {
            if self.at >= self.len { return None; }
            let index = self.at;
            self.at += 1.0;
            Some(RowView {
                    index,
                    id: unsafe { bench_id(index) },
                    label: unsafe { bench_label(index) },
                    class: unsafe { bench_class(index) },
                })
        }
    }
    /// What a handler may ask of the store.
    ///
    /// Methods rather than functions because the view's expression language has no
    /// free function calls, and written on `version` because that is the signal every
    /// one of them ends by bumping. Life's arrangement, and for its reasons.
    ///
    /// The server's half is every method with an empty body: its handlers are never
    /// called, because the server renders markup and the browser's copy of the island
    /// is what runs.
    trait Control {
        /// Replaces the table with a thousand fresh rows.
        fn run(self);
        /// Replaces it with ten thousand.
        fn runlots(self);
        /// Adds a thousand to whatever is there.
        fn add(self);
        /// Marks every tenth row from the first.
        fn update(self);
        /// Empties the table.
        fn clear(self);
        /// Exchanges the second row with the nine hundred and ninety-ninth.
        fn swaprows(self);
        /// Selects the row at `index`, which is the only selection there is.
        fn select(self, index: f64);
        /// Takes the row at `index` out.
        fn remove(self, index: f64);
    }
    impl Control for ::view_abi::Sig<f64> {
        fn run(self) { unsafe { bench_clear() }; append(BATCH); bump(self); }
        fn runlots(self) {
            unsafe { bench_clear() };
            append(BIG_BATCH);
            bump(self);
        }
        fn add(self) { append(BATCH); bump(self); }
        fn update(self) {
            let len = unsafe { bench_len() };
            let mut at = 0.0;
            while at < len { unsafe { bench_bang(at) }; at += EVERY; }
            bump(self);
        }
        fn clear(self) { unsafe { bench_clear() }; bump(self); }
        fn swaprows(self) {
            if unsafe { bench_len() } > SWAP_HIGH {
                unsafe { bench_swap(SWAP_LOW, SWAP_HIGH) };
                bump(self);
            }
        }
        fn select(self, index: f64) {
            unsafe { bench_select(index) };
            bump(self);
        }
        fn remove(self, index: f64) {
            unsafe { bench_remove(index) };
            bump(self);
        }
    }
    /// Says that the rows changed, which is what rebuilds the table.
    fn bump(version: ::view_abi::Sig<f64>) {
        version.set(version.get() + 1.0);
    }
    /// Puts `count` fresh rows on the end of the store.
    ///
    /// The three words are picked here and joined there: this crate has no heap to
    /// build `"pretty red pony"` in, so the store is handed the parts. Which parts is
    /// the decision, and the decision is Rust.
    fn append(count: i32) {
        let mut made = 0;
        while made < count {
            let adjective = ADJECTIVES[random(ADJECTIVES.len())];
            let colour = COLOURS[random(COLOURS.len())];
            let noun = NOUNS[random(NOUNS.len())];
            unsafe { bench_push(adjective, colour, noun) };
            made += 1;
        }
    }
    /// A number below `max`, drawn the way the benchmark draws one.
    ///
    /// `Math.round(Math.random() * 1000) % max` exactly, rounding and all. It is a
    /// poor generator -- a thousand possible draws folded into twenty-five buckets is
    /// visibly lumpy -- and it is the one every published entry uses, so it is the one
    /// that produces a comparable amount of text.
    fn random(max: usize) -> usize {
        (math_round(math_random() * 1000.0) as usize) % max
    }
    #[doc = " `Math.random()`."]
    #[link_section = "rcgjs.ext.2.c-.0,11.Math.random"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn math_random() -> f64 {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`math_random` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc = " `Math.round(x)`."]
    #[link_section = "rcgjs.ext.2.c-.0,10.Math.round"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn math_round(x: f64) -> f64 {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`math_round` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[allow(improper_ctypes)]
    unsafe extern "C" {
        /// How many rows the store holds.
        fn bench_len()
        -> f64;
        /// The id of the row at `index`.
        fn bench_id(index: f64)
        -> f64;
        /// Its label.
        fn bench_label(index: f64)
        -> &'static str;
        /// The class its `<tr>` carries, which is the selection made visible.
        fn bench_class(index: f64)
        -> &'static str;
        /// Puts a row on the end, labelled with the three words joined by spaces and
        /// numbered with the store's next id.
        fn bench_push(adjective: &str, colour: &str, noun: &str);
        /// Marks the label of the row at `index`, which is what an update does to it.
        fn bench_bang(index: f64);
        /// Exchanges the rows at two positions.
        fn bench_swap(a: f64, b: f64);
        /// Takes the row at `index` out.
        fn bench_remove(index: f64);
        /// Makes the row at `index` the selected one.
        fn bench_select(index: f64);
        /// Empties the store and forgets the selection.
        fn bench_clear();
    }
    #[unsafe(no_mangle)]
    #[allow(unused_braces)]
    pub fn __island_bench() -> ::view_abi::Node {
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<div class=\"container\"><div class=\"jumbotron\"><div class=\"row\"><div class=\"col-md-6\"><h1>Topcoat-island</h1></div><div class=\"col-md-6\"><div class=\"row\"><div class=\"col-sm-6 smallpad\"><button type=\"button\" class=\"btn btn-primary btn-block\" id=\"run\">Create 1,000 rows</button></div><div class=\"col-sm-6 smallpad\"><button type=\"button\" class=\"btn btn-primary btn-block\" id=\"runlots\">Create 10,000 rows</button></div><div class=\"col-sm-6 smallpad\"><button type=\"button\" class=\"btn btn-primary btn-block\" id=\"add\">Append 1,000 rows</button></div><div class=\"col-sm-6 smallpad\"><button type=\"button\" class=\"btn btn-primary btn-block\" id=\"update\">Update every 10th row</button></div><div class=\"col-sm-6 smallpad\"><button type=\"button\" class=\"btn btn-primary btn-block\" id=\"clear\">Clear</button></div><div class=\"col-sm-6 smallpad\"><button type=\"button\" class=\"btn btn-primary btn-block\" id=\"swaprows\">Swap Rows</button></div></div></div></div></div><table class=\"table table-hover table-striped test-data\"><tbody></tbody></table><span class=\"preloadicon glyphicon glyphicon-remove\" aria-hidden=\"true\"></span></div>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "cccnccc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cccnccnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cccnccnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cccnccnnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cccnccnnnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cccnccnnnnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnc",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                }],
                    events: &["click"],
                };
            static __TC_TEMPLATE_1: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<tr><td class=\"col-md-1\"></td><td class=\"col-md-4\"><a></a></td><td class=\"col-md-1\"><a><span class=\"glyphicon glyphicon-remove\" aria-hidden=\"true\"></span></a></td><td class=\"col-md-6\"></td></tr>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Attribute,
                                    name: "class",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "c",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnc",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &["click"],
                };
            let version = ::view_abi::signal(0u32, 0.0);
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::handler(&__tc_root_0, 0u32,
                move |_e: &::view_abi::Event| version.run());
            ::view_abi::handler(&__tc_root_0, 1u32,
                move |_e: &::view_abi::Event| version.runlots());
            ::view_abi::handler(&__tc_root_0, 2u32,
                move |_e: &::view_abi::Event| version.add());
            ::view_abi::handler(&__tc_root_0, 3u32,
                move |_e: &::view_abi::Event| version.update());
            ::view_abi::handler(&__tc_root_0, 4u32,
                move |_e: &::view_abi::Event| version.clear());
            ::view_abi::handler(&__tc_root_0, 5u32,
                move |_e: &::view_abi::Event| version.swaprows());
            ::view_abi::effect(&__tc_root_0, 6u32,
                move ||
                    {
                        let __tc_rows = ::view_abi::list();
                        for row in rows(version.get()) {
                            ::view_abi::push(&__tc_rows,
                                ::view_abi::content({
                                        let index = row.index;
                                        let __tc_root_1 = ::view_abi::template(&__TC_TEMPLATE_1);
                                        ::view_abi::hole(&__tc_root_1, 0u32, row.class);
                                        ::view_abi::hole(&__tc_root_1, 1u32, row.id);
                                        ::view_abi::handler(&__tc_root_1, 2u32,
                                            move |_e: &::view_abi::Event| version.select(index));
                                        ::view_abi::hole(&__tc_root_1, 3u32, row.label);
                                        ::view_abi::handler(&__tc_root_1, 4u32,
                                            move |_e: &::view_abi::Event| version.remove(index));
                                        __tc_root_1
                                    }));
                        }
                        __tc_rows
                    });
            __tc_root_0
        }
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub struct BenchProps {}
    #[doc(hidden)]
    pub mod __bench_island {
        #[allow(non_camel_case_types)]
        #[allow(dead_code)]
        pub struct AnIslandCannotBeNestedInsideAnotherIsland;
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub fn bench(_: __bench_island::AnIslandCannotBeNestedInsideAnotherIsland)
        -> ::view_abi::Node {
        loop {}
    }
}
#[path = "../island/counter.rs"]
mod counter {
    //! The counter island: one view, compiled twice.
    //!
    //! This file is a module of two crates. The server crate reads it as ordinary
    //! Topcoat, where the view renders to HTML carrying the hydration keys the
    //! client finds its nodes by. The client crate reads it with `topcoat_client`
    //! set, where the same view lowers to a dom-expressions template and the whole
    //! crate is compiled to JavaScript by the rustc backend.
    //!
    //! Nothing below is written for either target in particular. The signal, the
    //! handlers, and the interpolation are the ones the runtime guide's counter
    //! uses; what changes is which compiler reads them.
    #[unsafe(no_mangle)]
    #[allow(unused_braces)]
    pub fn __island_counter(start: f64) -> ::view_abi::Node {
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<div class=\"island\"><p class=\"island-count\">count <!$><!/></p><div class=\"island-controls\"><button class=\"island-step\" type=\"button\">-1</button><button class=\"island-step\" type=\"button\">+1</button></div></div>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "ccn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnc",
                                    kind: ::view_abi::HoleKind::Property,
                                    name: "disabled",
                                    reactive: true,
                                    effect_group: 0u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cncn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &["click"],
                };
            let count = ::view_abi::signal(0u32, start);
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::effect(&__tc_root_0, 0u32, move || count.get());
            ::view_abi::effect(&__tc_root_0, 1u32,
                move || count.get() <= 0.0);
            ::view_abi::handler(&__tc_root_0, 2u32,
                move |_e: &::view_abi::Event| count.set(count.get() - 1.0));
            ::view_abi::handler(&__tc_root_0, 3u32,
                move |_e: &::view_abi::Event| count.set(count.get() + 1.0));
            __tc_root_0
        }
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub struct CounterProps {
        pub start: f64,
    }
    #[doc(hidden)]
    pub mod __counter_island {
        #[allow(non_camel_case_types)]
        #[allow(dead_code)]
        pub struct AnIslandCannotBeNestedInsideAnotherIsland;
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub fn counter(_:
            __counter_island::AnIslandCannotBeNestedInsideAnotherIsland)
        -> ::view_abi::Node {
        loop {}
    }
}
#[path = "../island/life.rs"]
mod life {
    //! The Game of Life island: a board the browser plays, stepped by compiled Rust.
    //!
    //! Like every island this file is a module of two crates. The server crate reads
    //! it as ordinary Topcoat and renders the first generation as HTML with the
    //! hydration keys in it; the client crate reads it with `topcoat_client` set and
    //! the rustc backend compiles the same view to JavaScript. What is new here is
    //! that the thing being compiled is a *program*: a wrapping neighbour count, a
    //! `match` over a pair that is the whole of Conway's rule, and a double-buffered
    //! step, none of which the old runtime expression language could say at all.
    //!
    //! # Where the board lives
    //!
    //! Nowhere in this crate. The island crate is `#![no_std]` with no heap and no
    //! mutable statics, so a `[u8; 640]` that survives from one generation to the
    //! next is not a thing it can declare. It borrows one instead: `__rt.board` hands
    //! back the `{ buf, off, len }` record a slice is, over an array the page keeps,
    //! and the backend indexes one as `record.buf[record.off + i]`. So `cur()` and
    //! `next()` below are a read and a write of the page's own array with nothing
    //! copied in either direction. See the showcase section of
    //! [`island-rt`](../src/island-rt.mjs).
    //!
    //! # What the view may say, and what that costs
    //!
    //! A `$( ... )` value -- a reactive hole or a handler -- is compiled by the
    //! server's expression language as well as by the client compiler, and that
    //! language has no free function calls: `f(a)` lowers to `f.call((a,))`, which is
    //! not a thing a Rust fn item is. It does have method calls. So everything a hole
    //! or a handler asks of the simulation is a method of [`Control`], written on the
    //! signal that the answer bumps -- `edits.toggle(..)` changes the board and says
    //! so, `generation.advance()` steps it and says so. Plain Rust is still plain
    //! Rust in the two places a view has it: a `for` loop's iterable and a `signal`
    //! initialiser.
    //!
    //! # Which hole re-runs when
    //!
    //! Two subscriptions, kept apart on purpose.
    //!
    //! * The driver hole reads `tick`, which a 20 Hz interval bumps. It is one number of text, so
    //!   running it 20 times a second costs nothing.
    //! * The grid's loop reads `generation` and `edits`, which move only when the board actually
    //!   changed. 640 rows are rebuilt then and not otherwise, so a paused board is a page doing no DOM
    //!   work at all.
    //!
    //! The clock runs at a fixed rate and the speed slider picks a stride over it,
    //! because an interval cannot be re-timed through the exports the page lends and
    //! starting a second one would leave the first running.
    //!
    //! # Why writing `generation` from the driver does not loop
    //!
    //! The driver runs inside an effect, and an effect that reads what it writes
    //! re-runs itself for ever unless the write makes the condition false. It does
    //! here: `stepped` holds the tick the last step happened at, so the re-run the
    //! write provokes finds `stepped == tick` and does nothing. That fixed point is
    //! also what keeps a change to `playing` or `speed` -- both read by the driver,
    //! both able to re-run it mid-tick -- from stepping the board a second time.
    //!
    //! # Why the first render matches on both sides
    //!
    //! The server renders the opening board and the client adopts that markup, so the
    //! two have to agree cell for cell. They agree because there is one
    //! [`initial_alive`], shared by both halves with no cfg on it: the server maps it
    //! over `0..640` and the client stamps the same function into the board before
    //! the first render reads it. What that produces is written down twice -- in the
    //! test at the foot of this file, against the server half, and in `smoke/life.mjs`,
    //! against the compiled client -- so neither side can drift alone.
    /// Columns of the board.
    const WIDTH: i32 = 32;
    /// Rows of it.
    const HEIGHT: i32 = 20;
    /// How many cells that is. The loop rebuilds all of them whenever the board
    /// changes, so it is the number to move if the page ever stutters.
    const CELLS: i32 = WIDTH * HEIGHT;
    /// The board being read, as [`island-rt`](../src/island-rt.mjs) numbers it.
    ///
    /// The four constants from here to [`NEIGHBOURS`] are the simulation's own, and
    /// the simulation is the client's half alone: the server renders the opening
    /// board from [`initial_alive`] and never steps it. So they carry the same
    /// `topcoat_client` gate their callers do, rather than sitting outside it and
    /// being dead code in every server build.
    const CUR: f64 = 1.0;
    /// The board being written. A generation depends on its neighbours *before* the
    /// step, so writing into the board being read would let the first half of a
    /// generation change the second half.
    const NEXT: f64 = 2.0;
    /// The number this island claims its one-time setup under.
    ///
    /// The same number as [`CUR`] rather than a new one, and that is the convention
    /// and not a coincidence: three islands share `claim`, the plan hands out board
    /// ids and nothing else, so an island claiming under its first board id is an
    /// island that invents no number at all.
    const CLAIM: f64 = CUR;
    /// The eight cells that surround a cell, as offsets from it.
    ///
    /// A table rather than a pair of nested loops, because the centre cell is not its
    /// own neighbour and a table says so by leaving `(0, 0)` out.
    const NEIGHBOURS: [(i32, i32); 8] =
        [(-1, -1), (0, -1), (1, -1), (-1, 0), (1, 0), (-1, 1), (0, 1),
                (1, 1)];
    /// The five living cells of a glider, as offsets from the cell it is stamped at.
    const GLIDER: [(i32, i32); 5] = [(1, 0), (2, 1), (0, 2), (1, 2), (2, 2)];
    /// Where the glider starts.
    const GLIDER_AT: (i32, i32) = (1, 1);
    /// The five living cells of an r-pentomino, which is the smallest pattern worth
    /// watching: it takes over a thousand generations to settle.
    const R_PENTOMINO: [(i32, i32); 5] =
        [(1, 0), (2, 0), (0, 1), (1, 1), (1, 2)];
    /// Where it starts, which is roughly the middle of the board.
    const R_PENTOMINO_AT: (i32, i32) = (15, 8);
    /// Whether the cell at `index` is alive before anything has stepped.
    ///
    /// Shared by both halves with no cfg on it, and that is the whole of the parity
    /// story: the server renders this over `0..CELLS` and the client stamps the same
    /// answers into the board before its first render reads it, so the markup the
    /// browser adopts is the markup it would have built.
    ///
    /// Integer arithmetic only. A float that reads back as `1` on one side and
    /// `1.0000000000000002` on the other would be a difference in the HTML.
    fn initial_alive(index: i32) -> bool {
        if !(0..CELLS).contains(&index) { return false; }
        let x = index % WIDTH;
        let y = index / WIDTH;
        covers(&GLIDER, x - GLIDER_AT.0, y - GLIDER_AT.1) ||
            covers(&R_PENTOMINO, x - R_PENTOMINO_AT.0, y - R_PENTOMINO_AT.1)
    }
    /// Whether `shape` has a living cell at the offset `(dx, dy)`.
    ///
    /// An indexed `while` rather than an iterator: this runs 640 times per pattern on
    /// the first render of both halves, and a plain loop is the same answer with
    /// nothing borrowed.
    fn covers(shape: &[(i32, i32)], dx: i32, dy: i32) -> bool {
        let mut at = 0;
        while at < shape.len() {
            let (ox, oy) = shape[at];
            if ox == dx && oy == dy { return true; }
            at += 1;
        }
        false
    }
    /// One cell of the board, as the grid's loop renders it.
    struct Cell {
        /// Which cell this is, which the button carries as its `value` so that one
        /// handler on the container can tell which one was clicked.
        index: i32,
        /// `"cell"` or `"cell on"`.
        class: &'static str,
    }
    #[automatically_derived]
    #[doc(hidden)]
    unsafe impl ::core::clone::TrivialClone for Cell { }
    #[automatically_derived]
    impl ::core::clone::Clone for Cell {
        #[inline]
        fn clone(&self) -> Cell {
            let _: ::core::clone::AssertParamIsClone<i32>;
            let _: ::core::clone::AssertParamIsClone<&'static str>;
            *self
        }
    }
    #[automatically_derived]
    impl ::core::marker::Copy for Cell { }
    /// How a cell in that state is dressed.
    ///
    /// Two `&'static str`s rather than a formatted string, because the island crate
    /// has no heap to format into.
    fn alive_class(alive: bool) -> &'static str {
        if alive { "cell on" } else { "cell" }
    }
    /// The event a handler is handed.
    ///
    /// The browser's compiler hands a handler a borrowed event and the server's hands
    /// it an owned one, so the type is declared once per target and the handler names
    /// only this. Naming the type in the parameter instead would name one target's
    /// type in both.
    type Ev<'a> = &'a ::view_abi::Event;
    /// The cells to draw, read straight out of the page's array.
    ///
    /// `tick` is taken and never read. It is what [`open`] needs to start the clock,
    /// and this loop is one of the two holes that might be the first to run: whichever
    /// of them gets there opens the island, so neither has to be written above the
    /// other. Taking it does not subscribe the loop to it -- only `get` does that --
    /// which is what keeps a 20 Hz clock from rebuilding 640 rows 20 times a second.
    fn cells(_generation: f64, _edits: f64, tick: ::view_abi::Sig<f64>)
        -> Cells {
        open(tick);
        Cells { at: 0, board: cur() }
    }
    /// The cells of the current board, one at a time.
    ///
    /// An iterator rather than a collection: there is no heap here to collect into.
    struct Cells {
        at: i32,
        board: &'static [u8],
    }
    impl Iterator for Cells {
        type Item = Cell;
        fn next(&mut self) -> Option<Cell> {
            if self.at >= CELLS { return None; }
            let index = self.at;
            self.at += 1;
            Some(Cell {
                    index,
                    class: alive_class(self.board[index as usize] != 0),
                })
        }
    }
    /// What a hole or a handler may ask of the simulation.
    ///
    /// Methods rather than functions because the view's expression language has no
    /// free function calls, and written on the signal each one bumps because that is
    /// the part a reader of the view needs: `edits.toggle(..)` says that a click
    /// changes the board, `generation.advance()` says that the step button moves the
    /// generation on.
    ///
    /// The server's half is every method with an empty body. Its handlers are never
    /// called -- the server renders markup and the browser's copy of the island is
    /// what runs -- and its driver renders the opening population, which is the one
    /// answer it has to get right.
    trait Control {
        /// Steps the board if this tick is one the speed asks for, and answers with
        /// the population either way. Written on `tick`.
        fn drive(self, playing: ::view_abi::Sig<f64>,
        speed: ::view_abi::Sig<f64>, stepped: ::view_abi::Sig<f64>,
        generation: ::view_abi::Sig<f64>)
        -> f64;
        /// Steps the board once, whatever the clock is doing. Written on
        /// `generation`.
        fn advance(self);
        /// Flips the cell the click came from, or does nothing if it came from
        /// between the cells. Written on `edits`.
        fn toggle(self, target: &'static str);
        /// Puts the opening board back. Written on `edits`.
        fn reset(self);
        /// Kills every cell. Written on `edits`.
        fn clear(self);
        /// Fills the board at random from `seed`. Written on `edits`.
        fn scatter(self, seed: f64);
        /// Takes the slider's position, which arrives as text. Written on `speed`.
        fn tune(self, target: &'static str);
    }
    impl Control for ::view_abi::Sig<f64> {
        fn drive(self, playing: ::view_abi::Sig<f64>,
            speed: ::view_abi::Sig<f64>, stepped: ::view_abi::Sig<f64>,
            generation: ::view_abi::Sig<f64>) -> f64 {
            open(self);
            let now = self.get();
            if playing.get() != 0.0 && now > 0.0 && stepped.get() != now &&
                    (now as i32) % stride(speed.get()) == 0 {
                let population = step(cur(), next());
                unsafe { board_swap(CUR, NEXT) };
                stepped.set(now);
                generation.set(generation.get() + 1.0);
                return population;
            }
            population_of(cur())
        }
        fn advance(self) {
            let _ = step(cur(), next());
            unsafe { board_swap(CUR, NEXT) };
            self.set(self.get() + 1.0);
        }
        fn toggle(self, target: &'static str) {
            let index = cell_index(target);
            if index < 0 { return; }
            let board = cur_mut();
            board[index as usize] =
                if board[index as usize] == 0 { 1 } else { 0 };
            self.set(self.get() + 1.0);
        }
        fn reset(self) { stamp(); self.set(self.get() + 1.0); }
        fn clear(self) {
            let board = cur_mut();
            let mut index = 0;
            while index < CELLS { board[index as usize] = 0; index += 1; }
            self.set(self.get() + 1.0);
        }
        fn scatter(self, seed: f64) {
            let mut state = seed as u32;
            if state == 0 { state = DEFAULT_SEED; }
            let board = cur_mut();
            let mut index = 0;
            while index < CELLS {
                state = xorshift(state);
                board[index as usize] =
                    if state % 100 < DENSITY { 1 } else { 0 };
                index += 1;
            }
            self.set(self.get() + 1.0);
        }
        fn tune(self, target: &'static str) {
            let steps = digits(target);
            if steps < 0 { return; }
            self.set(steps as f64);
        }
    }
    /// How many ticks the clock delivers a second.
    ///
    /// A fixed rate with a stride over it, rather than an interval the slider
    /// re-times: the page lends this island a clock it can start and nothing that
    /// stops one, so re-timing would mean a second interval beside the first.
    const TICKS_PER_SECOND: i32 = 20;
    /// How long that is, as the interval takes it.
    const TICK_MS: f64 = 1000.0 / TICKS_PER_SECOND as f64;
    /// How many cells in a hundred [`Control::scatter`] leaves alive. Below about a
    /// fifth a random board dies out; well above it, it jams.
    const DENSITY: u32 = 30;
    /// The state [`Control::scatter`] falls back to, because a seed of zero would
    /// leave [`xorshift`] producing zero for ever.
    const DEFAULT_SEED: u32 = 0x2545_f491;
    /// How many ticks apart two steps are, at `speed` steps a second.
    ///
    /// Clamped rather than trusted: `speed` arrives from a range input, and the text
    /// of an input is whatever the page was handed.
    fn stride(speed: f64) -> i32 {
        let steps = speed as i32;
        let steps =
            if steps < 1 {
                1
            } else if steps > TICKS_PER_SECOND {
                TICKS_PER_SECOND
            } else { steps };
        TICKS_PER_SECOND / steps
    }
    /// Opens the island: stamps the opening board, makes the second one, and starts
    /// the clock.
    ///
    /// Called by both of the holes that could be the first to run, and guarded by the
    /// page's `claim` so that only the first call does anything. An island's setup
    /// must be synchronous -- hydration's window is exactly one synchronous call
    /// stack -- and every call here is, so the first run of a hole is the right place
    /// for it.
    fn open(tick: ::view_abi::Sig<f64>) {
        if !unsafe { claim(CLAIM) } { return; }
        stamp();
        let _ = next();
        unsafe { clock_start(tick, TICK_MS) };
    }
    /// Writes the opening board.
    fn stamp() {
        let board = cur_mut();
        let mut index = 0;
        while index < CELLS {
            board[index as usize] = if initial_alive(index) { 1 } else { 0 };
            index += 1;
        }
    }
    /// The board being read.
    fn cur() -> &'static [u8] { unsafe { board_ro(CUR, CELLS as f64) } }
    /// The same board, to write.
    fn cur_mut() -> &'static mut [u8] { unsafe { board(CUR, CELLS as f64) } }
    /// The board the next generation is written into.
    fn next() -> &'static mut [u8] { unsafe { board(NEXT, CELLS as f64) } }
    /// How many of the eight neighbours of `(x, y)` are alive.
    ///
    /// The board wraps: a coordinate that runs off one edge comes back on the
    /// opposite one. That is what `rem_euclid` is for, and why it is not `%`. The two
    /// differ exactly on negative numbers, which is exactly the case that arises
    /// here: `-1 % w` is `-1`, but `(-1).rem_euclid(w)` is `w - 1`, the far edge.
    fn living_neighbours(board: &[u8], x: i32, y: i32) -> i32 {
        let mut alive = 0;
        let mut at = 0;
        while at < NEIGHBOURS.len() {
            let (dx, dy) = NEIGHBOURS[at];
            at += 1;
            let nx = (x + dx).rem_euclid(WIDTH);
            let ny = (y + dy).rem_euclid(HEIGHT);
            if board[(ny * WIDTH + nx) as usize] != 0 { alive += 1; }
        }
        alive
    }
    /// Writes the generation after `cur` into `next` and answers with how many cells
    /// it left alive.
    ///
    /// The rule itself is one `match` over a pair, which is the whole of Life: a
    /// living cell with two or three living neighbours stays alive, a dead cell with
    /// exactly three is born, and every other case is dead. It is also the thing this
    /// page is about -- the expression language the old client had could not say a
    /// `match` at all.
    fn step(cur: &[u8], next: &mut [u8]) -> f64 {
        let mut population = 0.0;
        let mut y = 0;
        while y < HEIGHT {
            let mut x = 0;
            while x < WIDTH {
                let index = (y * WIDTH + x) as usize;
                let alive =
                    match (cur[index] != 0, living_neighbours(cur, x, y)) {
                        (true, 2) | (true, 3) | (false, 3) => 1,
                        _ => 0,
                    };
                next[index] = alive;
                population += alive as f64;
                x += 1;
            }
            y += 1;
        }
        population
    }
    /// How many cells are alive.
    ///
    /// [`step`] already answers this for the board it wrote, so this is for the runs
    /// that stepped nothing.
    fn population_of(board: &[u8]) -> f64 {
        let mut alive = 0.0;
        let mut index = 0;
        while index < CELLS {
            if board[index as usize] != 0 { alive += 1.0; }
            index += 1;
        }
        alive
    }
    /// One step of a 32 bit xorshift generator.
    ///
    /// The caller carries the state, so this is a pure function of its input and a
    /// board can be replayed from the seed alone.
    fn xorshift(state: u32) -> u32 {
        let mut x = state;
        x ^= x << 13;
        x ^= x >> 17;
        x ^= x << 5;
        x
    }
    /// Which cell a click landed on, or `-1` for a click that landed on none.
    ///
    /// A delegated click reports the element it came FROM, and every cell is a
    /// `<button value=...>`, so the cell names itself and the grid needs one handler
    /// rather than 640 closures. A click on the gap between cells reports the
    /// container, which has no value, and `target_value` reads that back as the empty
    /// string -- which is why "no cell" is a case here rather than an accident.
    fn cell_index(target: &str) -> i32 {
        let index = digits(target);
        if index < 0 || index >= CELLS { return -1; }
        index
    }
    /// The decimal number `text` spells, or `-1` if it spells anything else.
    ///
    /// Written out rather than `str::parse`, which answers with a `Result` carrying
    /// an error type this crate has no reason to bring in. `as_bytes` on a `&str` is
    /// the host's own UTF-8 bytes, so this walks the string the page handed over with
    /// nothing decoded.
    fn digits(text: &str) -> i32 {
        let bytes = text.as_bytes();
        if bytes.is_empty() { return -1; }
        let mut value = 0;
        let mut at = 0;
        while at < bytes.len() {
            let byte = bytes[at];
            if !byte.is_ascii_digit() { return -1; }
            value = value * 10 + (byte - b'0') as i32;
            at += 1;
        }
        value
    }
    #[allow(improper_ctypes)]
    unsafe extern "C" {
        /// True the first time `id` asks and false afterwards.
        fn claim(id: f64)
        -> bool;
        /// A board of `len` cells, to write. The array is the page's and is kept, so
        /// what this hands back on the next frame is over the cells the last frame
        /// wrote.
        fn board(id: f64, len: f64)
        -> &'static mut [u8];
        /// The same board, to read. Two declarations because one borrows it mutably
        /// and the other does not, and an `extern` name is its JavaScript name.
        fn board_ro(id: f64, len: f64)
        -> &'static [u8];
        /// Exchanges what two boards hold. The `buf` moves and the record does not,
        /// so a slice taken before the exchange is over the new cells afterwards.
        fn board_swap(a: f64, b: f64);
        /// Moves `sig` on by one every `ms`, for ever.
        fn clock_start(sig: ::view_abi::Sig<f64>, ms: f64);
    }
    #[unsafe(no_mangle)]
    #[allow(unused_braces)]
    pub fn __island_life() -> ::view_abi::Node {
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<div class=\"island life\"><p class=\"life-readout\">generation <span class=\"life-generation\"></span> · population <span class=\"life-population\"></span></p><div class=\"life-grid\"></div><div class=\"island-controls\"><button class=\"island-step\" type=\"button\">play / pause</button><button class=\"island-step\" type=\"button\">step</button><button class=\"island-step\" type=\"button\">reset</button><button class=\"island-step\" type=\"button\">clear</button><button class=\"island-step\" type=\"button\">random</button></div><label class=\"life-speed\">speed <input class=\"life-rate\" type=\"range\" min=\"1\" max=\"20\" value=\"10\"></label></div>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "ccn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "ccnnn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnncn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnncnn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnncnnn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnncnnnn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnnncn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "input",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &["click", "input"],
                };
            static __TC_TEMPLATE_1: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<button type=\"button\" tabindex=\"-1\"></button>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Attribute,
                                    name: "class",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Attribute,
                                    name: "value",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &[],
                };
            let generation = ::view_abi::signal(0u32, 0.0);
            let edits = ::view_abi::signal(1u32, 0.0);
            let playing = ::view_abi::signal(2u32, 1.0);
            let speed = ::view_abi::signal(3u32, 10.0);
            let stepped = ::view_abi::signal(4u32, 0.0);
            let tick = ::view_abi::signal(5u32, 0.0);
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::effect(&__tc_root_0, 0u32, move || generation.get());
            ::view_abi::effect(&__tc_root_0, 1u32,
                move || tick.drive(playing, speed, stepped, generation));
            ::view_abi::handler(&__tc_root_0, 2u32,
                move |e: &::view_abi::Event|
                    { let e: Ev = e; edits.toggle(e.target_value()) });
            ::view_abi::effect(&__tc_root_0, 3u32,
                move ||
                    {
                        let __tc_rows = ::view_abi::list();
                        for cell in cells(generation.get(), edits.get(), tick) {
                            ::view_abi::push(&__tc_rows,
                                ::view_abi::content({
                                        let __tc_root_1 = ::view_abi::template(&__TC_TEMPLATE_1);
                                        ::view_abi::hole(&__tc_root_1, 0u32, cell.class);
                                        ::view_abi::hole(&__tc_root_1, 1u32, cell.index);
                                        __tc_root_1
                                    }));
                        }
                        __tc_rows
                    });
            ::view_abi::handler(&__tc_root_0, 4u32,
                move |_e: &::view_abi::Event|
                    playing.set(1.0 - playing.get()));
            ::view_abi::handler(&__tc_root_0, 5u32,
                move |_e: &::view_abi::Event| generation.advance());
            ::view_abi::handler(&__tc_root_0, 6u32,
                move |_e: &::view_abi::Event| edits.reset());
            ::view_abi::handler(&__tc_root_0, 7u32,
                move |_e: &::view_abi::Event| edits.clear());
            ::view_abi::handler(&__tc_root_0, 8u32,
                move |_e: &::view_abi::Event| edits.scatter(tick.get()));
            ::view_abi::handler(&__tc_root_0, 9u32,
                move |e: &::view_abi::Event|
                    { let e: Ev = e; speed.tune(e.target_value()) });
            __tc_root_0
        }
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub struct LifeProps {}
    #[doc(hidden)]
    pub mod __life_island {
        #[allow(non_camel_case_types)]
        #[allow(dead_code)]
        pub struct AnIslandCannotBeNestedInsideAnotherIsland;
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub fn life(_: __life_island::AnIslandCannotBeNestedInsideAnotherIsland)
        -> ::view_abi::Node {
        loop {}
    }
}
#[path = "../island/mines.rs"]
mod mines {
    //! The Minesweeper island: a 12x10 field of 15 mines, played entirely in
    //! compiled Rust.
    //!
    //! Like every island this file is a module of two crates. The server crate reads
    //! it as ordinary Topcoat and renders the HTML with the hydration keys in it; the
    //! client crate reads it with `topcoat_client` set and the rustc backend compiles
    //! the same view to JavaScript. What is new here is that the thing being compiled
    //! is a GAME: a seeded placement, a recursive flood fill and a win test, none of
    //! which the old expression language could have said at all.
    //!
    //! # Where the field lives
    //!
    //! In the host, as one board of 121 bytes reached through
    //! [`island-rt`](../src/island-rt.mjs)'s `board`/`board_ro` pair. The client crate
    //! is `#![no_std]` with no heap and no mutable statics, so a field that survives
    //! from one click to the next is not something it can declare -- but a `&[u8]` is
    //! the `{ buf, off, len }` record the host hands out, and the backend indexes one
    //! as `record.buf[record.off + i]`, so the array is read and written in place with
    //! nothing copied either way.
    //!
    //! One byte per cell, three bits of it used ([`MINE`], [`REVEALED`], [`FLAGGED`]).
    //! Adjacent counts are not stored: they are a function of the mines, and a stored
    //! copy is a second answer that can disagree with the first.
    //!
    //! # Why a click writes signals and nothing else
    //!
    //! A `$( ... )` value is compiled by the server's expression language as well as
    //! by the client compiler, and that language has no free function calls: `f(x)`
    //! lowers to `f.call((x,))`, which is the unstable `Fn` trait. So a handler can
    //! read an event and write a signal, and that is the whole of what these three do.
    //!
    //! Everything else -- placing the mines, flooding a region, counting what is
    //! left -- runs in the plain Rust a `for` loop's iterable is on both halves.
    //! [`cells`] and [`notes`] are those iterables, and both begin by calling
    //! [`apply`].
    //!
    //! # Why applying is guarded by a token
    //!
    //! Two loops read the same three signals, so a click re-runs both, and neither
    //! ordering is promised. Flagging is not idempotent -- applied twice it flags and
    //! unflags -- so [`apply`] does its work AT MOST ONCE per value of `version`,
    //! remembering the last one it acted on in the board's tail byte. Whichever loop
    //! runs first performs the move; the other finds it done and only reads. Nothing
    //! here writes a signal, so no render can cause another one.
    //!
    //! # Why the grid has one handler and not a hundred and twenty
    //!
    //! A `@click` on each cell of a `for` loop works -- a thunk built inside a loop
    //! snapshots its environment, see `examples/dom-tests/23_for_row_handlers.rs` --
    //! but it costs one closure per cell, rebuilt on every render. One handler on the
    //! container plus `value=(index)` on each cell is the cheaper shape, because
    //! `e.target_value()` compiles to `event.target.value || ""` -- the element the
    //! click came FROM, not the one the listener sits on -- so a single listener for
    //! the whole field can still tell the cells apart.
    //!
    //! # Why the server needs no board
    //!
    //! It renders the field before the first click, and that field is all zeroes. So
    //! the server's half of [`field`] is a zeroed array and every rule above runs over
    //! it unchanged, which makes the markup it writes the markup the client's first
    //! render would build -- parity by construction rather than by agreement.
    /// Columns of the field.
    const COLUMNS: i32 = 12;
    /// Rows of it.
    const ROWS: i32 = 10;
    /// Cells in it.
    const CELLS: i32 = COLUMNS * ROWS;
    /// How many of them hide a mine.
    const MINE_COUNT: i32 = 15;
    /// The board id the host keeps the field under. The ids are the islands' own:
    /// LIFE_CUR=1, LIFE_NEXT=2, SAND_CUR=3, SAND_NEXT=4, MINES=5.
    const BOARD: f64 = 5.0;
    /// Where the last applied `version` is remembered.
    ///
    /// One byte past the cells, which is what makes the board 121 rather than the 120
    /// a field alone needs. It is on the board rather than in a signal because a
    /// signal written from inside a render is a loop, and because this is exactly as
    /// long-lived as the field it guards.
    const TOKEN: usize = CELLS as usize;
    /// How long the board is, cells and tail together.
    const LEN: usize = TOKEN + 1;
    /// A cell hides a mine.
    const MINE: u8 = 1;
    /// A cell has been revealed.
    const REVEALED: u8 = 2;
    /// A cell has been flagged.
    const FLAGGED: u8 = 4;
    /// The eight cells that surround a cell, as offsets from it.
    ///
    /// A table rather than a pair of nested loops, because the centre cell is not its
    /// own neighbour and a table says so by leaving `(0, 0)` out.
    const NEIGHBOURS: [(i32, i32); 8] =
        [(-1, -1), (0, -1), (1, -1), (-1, 0), (1, 0), (-1, 1), (0, 1),
                (1, 1)];
    /// What a revealed cell shows, by how many mines are around it.
    ///
    /// A table of `&'static str` rather than a number formatted at render time: the
    /// client crate has no heap to format into, and a zero shows nothing at all,
    /// which no formatter would have done.
    const COUNTS: [&str; 9] = ["", "1", "2", "3", "4", "5", "6", "7", "8"];
    /// What a revealed cell is classed as, by the same count, so the digit can be
    /// coloured the way every minesweeper colours it.
    const OPEN: [&str; 9] =
        ["mines-cell open", "mines-cell open n1", "mines-cell open n2",
                "mines-cell open n3", "mines-cell open n4",
                "mines-cell open n5", "mines-cell open n6",
                "mines-cell open n7", "mines-cell open n8"];
    /// An untouched cell: what the server renders, and what the browser adopts.
    const HIDDEN: &str = "mines-cell";
    /// A flagged one.
    const FLAGGED_CLASS: &str = "mines-cell flagged";
    /// A revealed mine.
    const BLOWN: &str = "mines-cell blown";
    /// What a cell that shows nothing shows.
    const BLANK: &str = "";
    /// The mark a flag leaves.
    const FLAG_MARK: &str = "\u{2691}";
    /// The mark a mine leaves once it is out.
    const MINE_MARK: &str = "\u{2731}";
    /// A game still being played, as a class and a word.
    const PLAYING: &str = "mines-state";
    const PLAYING_WORD: &str = "playing";
    /// One that has been won.
    const WON: &str = "mines-state won";
    const WON_WORD: &str = "cleared";
    /// One that has been lost.
    const LOST: &str = "mines-state lost";
    const LOST_WORD: &str = "boom";
    /// What a click asks for. The view writes these numbers into `action`, as
    /// literals: a captured constant would be one more thing for the server's
    /// expression language to carry across, and three numbers with a table beside
    /// them are the same information.
    const RESTART: f64 = 0.0;
    /// Reveal the cell the click came from.
    const REVEAL: f64 = 1.0;
    /// Flag it, or take a flag off it.
    const FLAG: f64 = 2.0;
    /// How many draws a seeded placement may reject before it gives up and sweeps.
    ///
    /// A rejection loop over a full-period generator terminates, but "terminates"
    /// and "terminates soon" are different promises and this one runs in a browser.
    const DRAWS: i32 = 4096;
    /// The state a seed of zero is replaced by, because `xorshift` fed zero produces
    /// zero for ever.
    const DEFAULT_SEED: u32 = 0x2545_f491;
    /// The event a handler is handed.
    ///
    /// The browser's compiler hands a handler a borrowed event and the server's hands
    /// it an owned one, so the type is declared once per target and the handler names
    /// only this. The lifetime is the borrow on the client side and unused on the
    /// server's.
    type Ev<'a> = &'a ::view_abi::Event;
    /// What a click leaves behind: the `value` of the cell it came from.
    ///
    /// The server's signal holds a `String`, because that is the type its runtime can
    /// serialize; the browser's holds a `&'static str`, because its crate has no heap.
    /// One alias so that everything reading it is written once.
    type Pick = &'static str;
    /// The pick a fresh island starts with, which is no cell at all.
    fn no_pick() -> Pick { "" }
    /// One cell, as the view draws it.
    ///
    /// `index` is what makes the container handler work: it is written into the
    /// button's `value`, and a click reads it back out of `event.target`.
    struct Cell {
        index: f64,
        class: &'static str,
        label: &'static str,
    }
    #[automatically_derived]
    #[doc(hidden)]
    unsafe impl ::core::clone::TrivialClone for Cell { }
    #[automatically_derived]
    impl ::core::clone::Clone for Cell {
        #[inline]
        fn clone(&self) -> Cell {
            let _: ::core::clone::AssertParamIsClone<f64>;
            let _: ::core::clone::AssertParamIsClone<&'static str>;
            let _: ::core::clone::AssertParamIsClone<&'static str>;
            *self
        }
    }
    #[automatically_derived]
    impl ::core::marker::Copy for Cell { }
    /// The status line: how many mines are still unaccounted for, and how the game
    /// stands.
    struct Note {
        left: f64,
        class: &'static str,
        text: &'static str,
    }
    #[automatically_derived]
    #[doc(hidden)]
    unsafe impl ::core::clone::TrivialClone for Note { }
    #[automatically_derived]
    impl ::core::clone::Clone for Note {
        #[inline]
        fn clone(&self) -> Note {
            let _: ::core::clone::AssertParamIsClone<f64>;
            let _: ::core::clone::AssertParamIsClone<&'static str>;
            let _: ::core::clone::AssertParamIsClone<&'static str>;
            *self
        }
    }
    #[automatically_derived]
    impl ::core::marker::Copy for Note { }
    /// The field as it stands, borrowed for reading.
    ///
    /// The host keeps it, so a render sees what the last click left. Reading and
    /// writing are two `extern` declarations because they are two Rust types and an
    /// `extern` name is its JavaScript name; they are the same array.
    fn field() -> &'static [u8] { unsafe { board_ro(BOARD, LEN as f64) } }
    /// How many of the eight cells around `at` hide a mine.
    ///
    /// The field does not wrap: a neighbour off the edge is not a cell, which is what
    /// separates a minesweeper from a torus.
    fn around(cells: &[u8], at: i32) -> i32 {
        let x = at % COLUMNS;
        let y = at / COLUMNS;
        let mut found = 0;
        for &(dx, dy) in NEIGHBOURS.iter() {
            let nx = x + dx;
            let ny = y + dy;
            if !(0..COLUMNS).contains(&nx) || !(0..ROWS).contains(&ny) {
                continue;
            }
            if cells[(ny * COLUMNS + nx) as usize] & MINE != 0 { found += 1; }
        }
        found
    }
    /// How the cell at `index` draws.
    fn cell_at(cells: &[u8], index: i32) -> Cell {
        let cell = cells[index as usize];
        if cell & REVEALED != 0 {
            if cell & MINE != 0 {
                return Cell {
                        index: index as f64,
                        class: BLOWN,
                        label: MINE_MARK,
                    };
            }
            let count = around(cells, index) as usize;
            return Cell {
                    index: index as f64,
                    class: OPEN[count],
                    label: COUNTS[count],
                };
        }
        if cell & FLAGGED != 0 {
            return Cell {
                    index: index as f64,
                    class: FLAGGED_CLASS,
                    label: FLAG_MARK,
                };
        }
        Cell { index: index as f64, class: HIDDEN, label: BLANK }
    }
    /// How the game stands.
    ///
    /// Lost the moment a mine is revealed, won when every cell that is not a mine
    /// is. Both are read off the field rather than remembered, so a restart that
    /// zeroes the cells has nothing else to put back.
    fn status_of(cells: &[u8]) -> Note {
        let mut flagged = 0;
        let mut hidden = 0;
        let mut blown = false;
        for index in 0..CELLS {
            let cell = cells[index as usize];
            if cell & FLAGGED != 0 { flagged += 1; }
            if cell & MINE != 0 && cell & REVEALED != 0 { blown = true; }
            if cell & MINE == 0 && cell & REVEALED == 0 { hidden += 1; }
        }
        let left = (MINE_COUNT - flagged) as f64;
        if blown { return Note { left, class: LOST, text: LOST_WORD }; }
        if hidden == 0 { return Note { left, class: WON, text: WON_WORD }; }
        Note { left, class: PLAYING, text: PLAYING_WORD }
    }
    /// The cells of the field, one at a time.
    ///
    /// An iterator rather than a collection: the field is the host's and this crate
    /// has nowhere to copy it to.
    struct Cells {
        at: i32,
        cells: &'static [u8],
    }
    impl Iterator for Cells {
        type Item = Cell;
        fn next(&mut self) -> Option<Cell> {
            if self.at >= CELLS { return None; }
            let cell = cell_at(self.cells, self.at);
            self.at += 1;
            Some(cell)
        }
    }
    /// The status line, as an iterator of exactly one.
    ///
    /// A `for` loop's iterable is the only place in a view where a function may be
    /// called, and the status has to be recomputed when the field changes, so the one
    /// line is written as a loop over one note. See the module docs.
    struct Notes {
        at: i32,
        note: Note,
    }
    impl Iterator for Notes {
        type Item = Note;
        fn next(&mut self) -> Option<Note> {
            if self.at > 0 { return None; }
            self.at += 1;
            Some(self.note)
        }
    }
    /// The cells to draw, having first played whatever click `version` is counting.
    fn cells(version: f64, pick: Pick, action: f64, seed: f64) -> Cells {
        apply(version, pick, action, seed);
        Cells { at: 0, cells: field() }
    }
    /// The status line, having first played the same click.
    ///
    /// Calling [`apply`] from both loops is deliberate: it is the token that makes
    /// the move happen once, not the order the two loops run in.
    fn notes(version: f64, pick: Pick, action: f64, seed: f64) -> Notes {
        apply(version, pick, action, seed);
        Notes { at: 0, note: status_of(field()) }
    }
    fn apply(version: f64, pick: Pick, action: f64, seed: f64) {
        let cells = unsafe { board(BOARD, LEN as f64) };
        let token = (version as u32 & 0xff) as u8;
        if cells[TOKEN] == token { return; }
        cells[TOKEN] = token;
        if action == RESTART {
            for index in 0..CELLS { cells[index as usize] = 0; }
            return;
        }
        let at = parse_index(pick);
        if at < 0 { return; }
        if action == FLAG {
            if cells[at as usize] & REVEALED == 0 {
                cells[at as usize] ^= FLAGGED;
            }
            return;
        }
        if action != REVEAL || cells[at as usize] & FLAGGED != 0 { return; }
        if !placed(cells) { place(cells, at, seed); }
        reveal_at(cells, at);
        if cells[at as usize] & MINE != 0 {
            for index in 0..CELLS {
                if cells[index as usize] & MINE != 0 {
                    cells[index as usize] |= REVEALED;
                }
            }
        }
    }
    /// Whether the mines have been laid.
    ///
    /// Read off the field rather than remembered: [`place`] always lays at least one,
    /// and a restart zeroes every cell, so the mines themselves are the flag.
    fn placed(cells: &[u8]) -> bool {
        for index in 0..CELLS {
            if cells[index as usize] & MINE != 0 { return true; }
        }
        false
    }
    /// Lays [`MINE_COUNT`] mines, none of them within one cell of `safe`.
    ///
    /// Nine cells are kept clear rather than one, so the opening reveal is a zero and
    /// floods -- a first click that survives but shows a `4` is a worse first move
    /// than the game should hand out. That leaves 111 cells for 15 mines.
    fn place(cells: &mut [u8], safe: i32, seed: f64) {
        let mut state = (seed % 4_294_967_296.0) as u32;
        if state == 0 { state = DEFAULT_SEED; }
        let mut laid = 0;
        let mut draws = 0;
        while laid < MINE_COUNT && draws < DRAWS {
            draws += 1;
            state = xorshift(state);
            if lay(cells, (state % CELLS as u32) as i32, safe) { laid += 1; }
        }
        let mut at = 0;
        while laid < MINE_COUNT && at < CELLS {
            if lay(cells, at, safe) { laid += 1; }
            at += 1;
        }
    }
    /// Puts a mine at `at` unless there is one there or it is too close to `safe`,
    /// and answers whether it put one.
    fn lay(cells: &mut [u8], at: i32, safe: i32) -> bool {
        if cells[at as usize] & MINE != 0 || near(at, safe) { return false; }
        cells[at as usize] |= MINE;
        true
    }
    /// Whether `at` is `safe` or touches it.
    fn near(at: i32, safe: i32) -> bool {
        let dx = at % COLUMNS - safe % COLUMNS;
        let dy = at / COLUMNS - safe / COLUMNS;
        dx >= -1 && dx <= 1 && dy >= -1 && dy <= 1
    }
    /// One step of a 32 bit xorshift generator.
    ///
    /// The caller carries the state, so a field is a function of its seed alone and
    /// the same seed lays the same mines -- which is what lets a check assert where
    /// they are.
    fn xorshift(state: u32) -> u32 {
        let mut x = state;
        x ^= x << 13;
        x ^= x >> 17;
        x ^= x << 5;
        x
    }
    /// Reveals `at`, and everything a zero opens onto.
    ///
    /// The recursion is the point: a region is not a shape anything computes, it is
    /// wherever the zeroes reach. A flagged cell stops it, so a flag protects what it
    /// is on even when the flood arrives from behind.
    fn reveal_at(cells: &mut [u8], at: i32) {
        let cell = cells[at as usize];
        if cell & (REVEALED | FLAGGED) != 0 { return; }
        cells[at as usize] = cell | REVEALED;
        if cell & MINE != 0 || around(cells, at) != 0 { return; }
        let x = at % COLUMNS;
        let y = at / COLUMNS;
        for &(dx, dy) in NEIGHBOURS.iter() {
            let nx = x + dx;
            let ny = y + dy;
            if !(0..COLUMNS).contains(&nx) || !(0..ROWS).contains(&ny) {
                continue;
            }
            reveal_at(cells, ny * COLUMNS + nx);
        }
    }
    /// Which cell a click's `value` names, or `-1` for none.
    ///
    /// The digits are read out of the string's own bytes rather than parsed by the
    /// library, because what arrives is whatever was on the element the click came
    /// from and this has to answer "not a cell" for all of it.
    fn parse_index(pick: Pick) -> i32 {
        let bytes = pick.as_bytes();
        if bytes.is_empty() || bytes.len() > 3 { return -1; }
        let mut value = 0;
        for byte in bytes.iter() {
            let digit = *byte as i32 - b'0' as i32;
            if digit < 0 || digit > 9 { return -1; }
            value = value * 10 + digit;
        }
        if value >= CELLS { -1 } else { value }
    }
    #[allow(improper_ctypes)]
    unsafe extern "C" {
        /// The host's board under `id`, `len` bytes of it, borrowed to write.
        ///
        /// The array is made once and kept, so what a click writes is what the next
        /// render reads.
        fn board(id: f64, len: f64)
        -> &'static mut [u8];
        /// The same board, borrowed to read.
        fn board_ro(id: f64, len: f64)
        -> &'static [u8];
    }
    #[unsafe(no_mangle)]
    #[allow(unused_braces)]
    pub fn __island_mines(seed: f64) -> ::view_abi::Node {
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<div class=\"island mines\"><p class=\"mines-status\"></p><div class=\"mines-grid\"></div><div class=\"island-controls\"><button class=\"island-step\" type=\"button\">restart</button></div></div>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "c",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "contextmenu",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &["click", "contextmenu"],
                };
            static __TC_TEMPLATE_1: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<span><span class=\"mines-left\"></span><span class=\"mines-word\"></span></span>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Attribute,
                                    name: "class",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "c",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &[],
                };
            static __TC_TEMPLATE_2: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<button type=\"button\"></button>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Attribute,
                                    name: "class",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Attribute,
                                    name: "value",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &[],
                };
            let version = ::view_abi::signal(0u32, 0.0);
            let pick = ::view_abi::signal(1u32, no_pick());
            let action = ::view_abi::signal(2u32, 0.0);
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::effect(&__tc_root_0, 0u32,
                move ||
                    {
                        let __tc_rows = ::view_abi::list();
                        for note in
                            notes(version.get(), pick.get(), action.get(), seed) {
                            ::view_abi::push(&__tc_rows,
                                ::view_abi::content({
                                        let __tc_root_1 = ::view_abi::template(&__TC_TEMPLATE_1);
                                        ::view_abi::hole(&__tc_root_1, 0u32, note.class);
                                        ::view_abi::hole(&__tc_root_1, 1u32, note.left);
                                        ::view_abi::hole(&__tc_root_1, 2u32, note.text);
                                        __tc_root_1
                                    }));
                        }
                        __tc_rows
                    });
            ::view_abi::handler(&__tc_root_0, 1u32,
                move |e: &::view_abi::Event|
                    {
                        let e: Ev = e;
                        pick.set(e.target_value());
                        action.set(1.0);
                        version.set(version.get() + 1.0)
                    });
            ::view_abi::handler(&__tc_root_0, 2u32,
                move |e: &::view_abi::Event|
                    {
                        let e: Ev = e;
                        e.prevent_default();
                        pick.set(e.target_value());
                        action.set(2.0);
                        version.set(version.get() + 1.0)
                    });
            ::view_abi::effect(&__tc_root_0, 3u32,
                move ||
                    {
                        let __tc_rows = ::view_abi::list();
                        for cell in
                            cells(version.get(), pick.get(), action.get(), seed) {
                            ::view_abi::push(&__tc_rows,
                                ::view_abi::content({
                                        let __tc_root_2 = ::view_abi::template(&__TC_TEMPLATE_2);
                                        ::view_abi::hole(&__tc_root_2, 0u32, cell.class);
                                        ::view_abi::hole(&__tc_root_2, 1u32, cell.index);
                                        ::view_abi::hole(&__tc_root_2, 2u32, cell.label);
                                        __tc_root_2
                                    }));
                        }
                        __tc_rows
                    });
            ::view_abi::handler(&__tc_root_0, 4u32,
                move |_e: &::view_abi::Event|
                    { action.set(0.0); version.set(version.get() + 1.0) });
            __tc_root_0
        }
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub struct MinesProps {
        pub seed: f64,
    }
    #[doc(hidden)]
    pub mod __mines_island {
        #[allow(non_camel_case_types)]
        #[allow(dead_code)]
        pub struct AnIslandCannotBeNestedInsideAnotherIsland;
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub fn mines(_: __mines_island::AnIslandCannotBeNestedInsideAnotherIsland)
        -> ::view_abi::Node {
        loop {}
    }
}
#[path = "../island/nested.rs"]
mod nested {
    //! The nesting island: an island whose view calls components, which call
    //! components.
    //!
    //! Like `counter.rs` this file is a module of two crates, and nothing in it is
    //! written for either target in particular. What it adds to the counter is a
    //! component boundary: `labelled_card` is a `#[component(client)]` the island
    //! calls, and `badge` is one that `labelled_card` calls in turn, so the keys the
    //! server writes nest two contexts deep and the browser has to open the same two
    //! to find those nodes again.
    //!
    //! The shape is `contract/fixtures/corpus/07b-nested-components`'s
    //! `nested_in_element` case, which the parity fixture
    //! `contract/parity/fixtures/nested` measures against. Its keys are `0` for the
    //! island's own root, `10` for the card the call at slot 1 opens, and `110` for
    //! the badge the call at slot 1 of THAT context opens.
    //!
    //! The signal and the button are here so the fixture measures a live island
    //! rather than a static one: a click must change the count and touch nothing
    //! inside the two components. The components themselves are deliberately not
    //! reactive, because a signal prop and a `$(...)` prop value are separate
    //! features and folding them in would make one failure look like another.
    #[allow(dead_code)]
    pub struct BadgeProps<'__tc_props> {
        pub label: &'__tc_props str,
    }
    #[allow(dead_code)]
    #[allow(unused_braces)]
    pub fn badge(props: BadgeProps<'_>) -> ::view_abi::Node {
        let BadgeProps { label } = props;
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<span class=\"badge\"></span>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &[],
                };
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::hole(&__tc_root_0, 0u32, label);
            __tc_root_0
        }
    }
    #[allow(dead_code)]
    pub struct LabelledCardProps<'__tc_props> {
        pub title: &'__tc_props str,
    }
    #[allow(dead_code)]
    #[allow(unused_braces)]
    pub fn labelled_card(props: LabelledCardProps<'_>) -> ::view_abi::Node {
        let LabelledCardProps { title } = props;
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<section class=\"card\"><h2></h2><!$><!/></section>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "c",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::Component,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &[],
                };
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::hole(&__tc_root_0, 0u32, title);
            ::view_abi::component(&__tc_root_0, 1u32,
                move || badge(BadgeProps { label: "Active" }));
            __tc_root_0
        }
    }
    #[unsafe(no_mangle)]
    #[allow(unused_braces)]
    pub fn __island_nested(start: f64) -> ::view_abi::Node {
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<div class=\"island\"><p class=\"island-count\">count <!$><!/></p><!$><!/><div class=\"island-controls\"><button class=\"island-step\" type=\"button\">+1</button></div></div>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "ccn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::Component,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &["click"],
                };
            let count = ::view_abi::signal(0u32, start);
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::effect(&__tc_root_0, 0u32, move || count.get());
            ::view_abi::component(&__tc_root_0, 1u32,
                move ||
                    labelled_card(LabelledCardProps { title: "Profile" }));
            ::view_abi::handler(&__tc_root_0, 2u32,
                move |_e: &::view_abi::Event| count.set(count.get() + 1.0));
            __tc_root_0
        }
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub struct NestedProps {
        pub start: f64,
    }
    #[doc(hidden)]
    pub mod __nested_island {
        #[allow(non_camel_case_types)]
        #[allow(dead_code)]
        pub struct AnIslandCannotBeNestedInsideAnotherIsland;
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub fn nested(_:
            __nested_island::AnIslandCannotBeNestedInsideAnotherIsland)
        -> ::view_abi::Node {
        loop {}
    }
}
#[path = "../island/panel.rs"]
mod panel {
    //! A component that takes child content, so the page exercises the side buffer
    //! a component's children are rendered into.
    //!
    //! Child content is a prop, so it is built where the component is CALLED rather
    //! than where the component renders it. The server therefore renders it aside
    //! before it enters the component, and hands the component's own view a stand-in
    //! that writes those bytes wherever the content appears. That is what puts the
    //! content's hydration keys before the component's own boundary, which is the
    //! order an eager argument has on both sides.
    //!
    //! It is called from the nested island's PAGE rather than from inside an island:
    //! the client emitter does not lower child content at a call site yet, so this
    //! declares both halves and exercises the server's buffer. The client half is
    //! compiled and dropped, because nothing calls it.
    #[allow(dead_code)]
    pub struct PanelProps<'__tc_props> {
        pub title: &'__tc_props str,
        pub child: ::view_abi::Node,
    }
    #[allow(dead_code)]
    #[allow(unused_braces)]
    pub fn panel(props: PanelProps<'_>) -> ::view_abi::Node {
        let PanelProps { title, child } = props;
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<section class=\"panel\"><h2></h2><!$><!/></section>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "c",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &[],
                };
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::hole(&__tc_root_0, 0u32, title);
            ::view_abi::hole(&__tc_root_0, 1u32, child);
            __tc_root_0
        }
    }
}
#[path = "../island/sand.rs"]
mod sand {
    //! The falling sand island: a drawing surface whose grains fall, and the first
    //! thing in this repository that paints.
    //!
    //! Like the other islands this file is a module of two crates. What is new here
    //! is the SURFACE: the browser half owns a `<canvas>` and every pixel that lands
    //! on it is a `fillRect` the compiled Rust asked for, through a `#[js_extern]`
    //! block of eight declarations and nothing else. No drawing loop was written in
    //! JavaScript, and there is none to write.
    //!
    //! # What is compiled Rust
    //!
    //! The physics. A generation reads the current board and writes the next one:
    //! a grain falls straight down if it can, otherwise diagonally, and the side it
    //! prefers comes from a hash of its own coordinates and the frame number. That
    //! is the whole of [`fall`], and it is the reason the toy is here rather than a
    //! `requestAnimationFrame` loop with a `Math.random()` in it.
    //!
    //! The painting decision is compiled Rust too, and it is the interesting half:
    //! a frame paints the cells whose value CHANGED and no others, which is a
    //! comparison of two boards rather than a redraw. A full 60x40 redraw would be
    //! 2400 `fillRect` calls a frame; a settled pile is zero.
    //!
    //! The pointer arithmetic is compiled Rust as well. What crosses from the
    //! browser is `clientX` and `clientY`, untranslated; turning those into a cell
    //! is [`deposit`]'s subtraction of the canvas's own rectangle, read through two
    //! more declarations.
    //!
    //! # What the host module lends it
    //!
    //! Two things a declared interface cannot say.
    //!
    //! A PLACE TO KEEP A BOARD. This crate is `#![no_std]` with no heap and no
    //! mutable statics, so a `[u8; 2400]` that survives from one frame to the next
    //! is not something it can declare. It can borrow one: `board` hands back the
    //! `{ buf, off, len }` record a slice is, over an array the host keeps, and the
    //! backend indexes one as `record.buf[record.off + i]`. So the boards below are
    //! read and written in place, with nothing copied in either direction.
    //!
    //! TWO CALLBACKS. A clock and a pointer both call a function, and handing
    //! JavaScript a function means handing it a compiled Rust closure. So both are
    //! made in [`island-rt`](../src/island-rt.mjs) and handed the signals to write.
    //! Neither knows anything about sand.
    //!
    //! # Two holes, and why they do not overlap
    //!
    //! The view has two reactive holes and they subscribe to disjoint sets of
    //! signals, which is what keeps a pointer move off the physics and a frame off
    //! the pointer.
    //!
    //! [`frame`] reads `tick` and nothing else, so the clock is the only thing that
    //! runs a generation. [`deposit`] reads `px`, `py`, `pdown` and `brush`, so the
    //! pointer and the brush picker are the only things that stamp. Both are written
    //! as a `for` over an iterator of one value, because a `$( .. )` is compiled by
    //! the server's expression language as well as by the client's and that language
    //! has no free function calls; a loop's iterable is plain Rust on both halves.
    //!
    //! # Why setup is safe where it is
    //!
    //! An island's setup must be synchronous: hydration's window is exactly one
    //! synchronous call stack, and anything resuming after a suspension point finds
    //! it closed and silently rebuilds the DOM instead of adopting the server's.
    //! Finding the canvas, registering four listeners and opening an interval are
    //! all synchronous calls, so all of them are made on the first run of the frame
    //! hole's effect, inside that window. Nothing can arrive during it: a pointer
    //! event and a timer are both delivered in later tasks. So no mount hook is
    //! needed, and none was added.
    /// Columns of the grid.
    ///
    /// The grid is the browser's alone -- the server renders an empty canvas and a
    /// blank readout -- so this constant and the three below it carry the client
    /// gate their callers carry. `test` joins the gate because the parity test at
    /// the foot of the file multiplies them back out against the literals in the
    /// view's `width` and `height`, and that test is a server-side one.
    pub const COLS: usize = 60;
    /// Rows of it.
    pub const ROWS: usize = 40;
    /// Cells in it, which is also the length the host makes its boards.
    pub const CELLS: usize = COLS * ROWS;
    /// How many pixels wide and tall one cell is drawn.
    ///
    /// The canvas is `COLS * SCALE` by `ROWS * SCALE`, and the two numbers in the
    /// view's `width` and `height` are that product written out: an attribute is a
    /// literal on both halves and cannot be computed from these.
    pub const SCALE: f64 = 6.0;
    /// An empty cell.
    pub const EMPTY: u8 = 0;
    /// A grain of sand, which falls.
    pub const SAND: u8 = 1;
    /// A wall, which does not.
    pub const WALL: u8 = 2;
    /// What each cell value is painted.
    ///
    /// `EMPTY`'s colour is the canvas's own background rather than transparent,
    /// because a cell is repainted when it EMPTIES and clearing one at a time would
    /// be a second declaration for no gain. `.sand-canvas` carries the same colour
    /// in `demo.css`, so an unpainted cell and an emptied one look alike.
    pub const COLOURS: [&str; 3] = ["#1d1d22", "#dcb173", "#6c7080"];
    /// What each brush is called, in the order the picker's buttons set.
    ///
    /// One table, read by both halves: the server renders the name of the brush a
    /// fresh island starts with, and the browser's first run has to reach the same
    /// string or the markup it adopts is not the markup it would have built.
    pub const BRUSHES: [&str; 3] = ["sand", "wall", "erase"];
    /// The canvas the grid is drawn on.
    const CANVAS: &str = ".sand-canvas";
    /// The rendering context asked for.
    const CONTEXT: &str = "2d";
    /// The pointer events the island listens for.
    ///
    /// `pointerleave` as well as `pointerup`, because a button released outside the
    /// canvas is never reported to it and the last thing the sink saw would stay
    /// held down for ever.
    const POINTER: [&str; 4] =
        ["pointerdown", "pointermove", "pointerup", "pointerleave"];
    /// How long a frame lasts, in milliseconds.
    const FRAME_MS: f64 = 33.0;
    /// How far the brush reaches from the cell under the pointer.
    const REACH: i32 = 2;
    /// The board a frame reads and the pointer writes.
    ///
    /// Also the island's claim number. The board ids are handed out by the page's
    /// plan and are unique per island already, so borrowing one of them is a claim
    /// that cannot collide with another island's; a number of its own would be a
    /// second thing to keep unique.
    const CUR: f64 = 3.0;
    /// The board a frame writes, which becomes the current one when they exchange.
    const NEXT: f64 = 4.0;
    /// The number of grains a frame ends with, as an iterator of one.
    ///
    /// An iterator because the view reaches this through a `for`, which is where a
    /// hole may call a function.
    ///
    /// A flag rather than an `Option` the iterator takes from, and that is measured:
    /// `Option::take` lowers to `__rt.overwrite`, which `src/island-rt.mjs` does not
    /// supply, so a `take` here fails the build's shim check. `Hits` counts too.
    pub struct Grains {
        count: f64,
        given: bool,
    }
    impl Iterator for Grains {
        type Item = f64;
        fn next(&mut self) -> Option<f64> {
            if self.given { return None; }
            self.given = true;
            Some(self.count)
        }
    }
    /// The brush's name, as an iterator of one.
    pub struct Brush {
        name: &'static str,
        given: bool,
    }
    impl Iterator for Brush {
        type Item = &'static str;
        fn next(&mut self) -> Option<&'static str> {
            if self.given { return None; }
            self.given = true;
            Some(self.name)
        }
    }
    /// What the brush the picker last chose is called.
    fn brush_name(brush: f64) -> &'static str {
        let pick = brush as usize;
        if pick < BRUSHES.len() { BRUSHES[pick] } else { BRUSHES[0] }
    }
    /// What the brush the picker last chose puts in a cell.
    fn brush_value(brush: f64) -> u8 {
        match brush as u32 { 0 => SAND, 1 => WALL, _ => EMPTY, }
    }
    /// Which way a grain at `col`, `row` leans on frame `tick` when it cannot fall
    /// straight down.
    ///
    /// A hash rather than a generator, and that is the point rather than a
    /// shortcut: an island has nowhere to keep a seed between frames, and a toy that
    /// asked the host for a random number would have put the one interesting
    /// decision in JavaScript. This is a function of the three numbers it is given,
    /// so the same board on the same frame falls the same way twice, which is what
    /// lets a check assert a pile rather than look at one.
    fn leans_left(col: usize, row: usize, tick: f64) -> bool {
        let mut mixed =
            (col as u32).wrapping_mul(374_761_393) ^
                    (row as u32).wrapping_mul(668_265_263) ^
                (tick as u32).wrapping_mul(2_246_822_519);
        mixed ^= mixed >> 13;
        mixed = mixed.wrapping_mul(1_274_126_177);
        ((mixed ^ (mixed >> 16)) & 1) == 0
    }
    /// Moves every grain of `cells` one step, bottom row first.
    ///
    /// Bottom first is what makes a fall one cell per frame rather than the whole
    /// column at once: a grain moves into a row that has already been settled, so
    /// nothing is moved twice on the same pass. The bottom row itself is never a
    /// source, which is what makes a pile.
    fn fall(cells: &mut [u8], tick: f64) {
        for row in (0..ROWS - 1).rev() {
            for col in 0..COLS {
                let from = row * COLS + col;
                if cells[from] != SAND { continue; }
                let below = from + COLS;
                if cells[below] == EMPTY {
                    cells[below] = SAND;
                    cells[from] = EMPTY;
                    continue;
                }
                let first = if leans_left(col, row, tick) { -1 } else { 1 };
                if slide(cells, col, below, first) ||
                        slide(cells, col, below, -first) {
                    cells[from] = EMPTY;
                }
            }
        }
    }
    /// Puts a grain in the cell one row down and `step` columns across, if that cell
    /// exists and is empty. Answers whether it did.
    fn slide(cells: &mut [u8], col: usize, below: usize, step: i32) -> bool {
        let across = col as i32 + step;
        if across < 0 || across >= COLS as i32 { return false; }
        let into = (below as i32 + step) as usize;
        if cells[into] != EMPTY { return false; }
        cells[into] = SAND;
        true
    }
    /// Puts `value` in every cell the brush covers at `col`, `row`, and paints the
    /// ones that changed.
    ///
    /// The stamp paints rather than leaving it to the next frame, and it has to: a
    /// frame paints the difference between the board it read and the board it wrote,
    /// and a cell the pointer changed is the same in both. Painting here is also
    /// what makes drawing feel immediate instead of arriving up to a frame late.
    fn stamp(context: JsValue, col: i32, row: i32, value: u8) {
        let cells = unsafe { board(CUR, CELLS as f64) };
        let mut styled = false;
        for down in 0..REACH * 2 + 1 {
            for across in 0..REACH * 2 + 1 {
                let step_down = down - REACH;
                let step_across = across - REACH;
                if step_down * step_down + step_across * step_across >
                        REACH * REACH {
                    continue;
                }
                let at_col = col + step_across;
                let at_row = row + step_down;
                if at_col < 0 || at_row < 0 || at_col >= COLS as i32 ||
                        at_row >= ROWS as i32 {
                    continue;
                }
                let index = at_row as usize * COLS + at_col as usize;
                if cells[index] == value { continue; }
                cells[index] = value;
                if !styled {
                    set_fill_style(context, COLOURS[value as usize]);
                    styled = true;
                }
                fill_rect(context, at_col as f64 * SCALE,
                    at_row as f64 * SCALE, SCALE, SCALE);
            }
        }
    }
    fn frame(tick: f64, clock: ::view_abi::Sig<f64>, x: ::view_abi::Sig<f64>,
        y: ::view_abi::Sig<f64>, down: ::view_abi::Sig<f64>) -> Grains {
        let canvas = query_selector(CANVAS);
        if unsafe { claim(CUR) } {
            let sink = unsafe { pointer_sink(x, y, down) };
            for kind in POINTER { add_listener(canvas, kind, sink); }
            unsafe { clock_start(clock, FRAME_MS) };
        }
        let context = get_context(canvas, CONTEXT);
        let cur = unsafe { board_ro(CUR, CELLS as f64) };
        let next = unsafe { board(NEXT, CELLS as f64) };
        for index in 0..CELLS { next[index] = cur[index]; }
        fall(next, tick);
        let mut grains = 0u32;
        let mut styled = 255u8;
        for index in 0..CELLS {
            let value = next[index];
            if value == SAND { grains += 1; }
            if value == cur[index] { continue; }
            if value != styled {
                set_fill_style(context, COLOURS[value as usize]);
                styled = value;
            }
            fill_rect(context, (index % COLS) as f64 * SCALE,
                (index / COLS) as f64 * SCALE, SCALE, SCALE);
        }
        unsafe { board_swap(CUR, NEXT) };
        Grains { count: grains as f64, given: false }
    }
    fn deposit(x: f64, y: f64, down: f64, brush: f64) -> Brush {
        if down != 0.0 {
            let canvas = query_selector(CANVAS);
            let rect = bounding_rect(canvas);
            let col = ((x - rect_left(rect)) / SCALE) as i32;
            let row = ((y - rect_top(rect)) / SCALE) as i32;
            stamp(get_context(canvas, CONTEXT), col, row, brush_value(brush));
        }
        Brush { name: brush_name(brush), given: false }
    }
    /// An opaque JavaScript value: an element, a context, a rectangle, a function.
    ///
    /// One machine word behind a `repr(transparent)`, for the two reasons
    /// `view_abi::Node` gives: a zero sized value is folded away, and an ordinary one
    /// field struct is rebuilt field by field, so a JavaScript object passed through
    /// one would come back with its field undefined.
    #[repr(transparent)]
    struct JsValue {
        #[allow(dead_code)]
        handle: u32,
    }
    #[automatically_derived]
    #[doc(hidden)]
    unsafe impl ::core::clone::TrivialClone for JsValue { }
    #[automatically_derived]
    impl ::core::clone::Clone for JsValue {
        #[inline]
        fn clone(&self) -> JsValue {
            let _: ::core::clone::AssertParamIsClone<u32>;
            *self
        }
    }
    #[automatically_derived]
    impl ::core::marker::Copy for JsValue { }
    #[doc = " `document.querySelector(selector)`."]
    #[doc = ""]
    #[doc =
    " Called once per frame and once per stamp rather than held. Caching the"]
    #[doc =
    " canvas would need somewhere to put it, and the only somewhere is a host"]
    #[doc =
    " slot; the shared `__rt` surface offers none on purpose, and two property"]
    #[doc = " lookups a frame is not what a frame costs."]
    #[link_section = "rcgjs.ext.2.c-.0,22.document.querySelector"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn query_selector(selector: &str) -> JsValue {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`query_selector` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc = " `canvas.getContext(kind)`."]
    #[link_section = "rcgjs.ext.2.s-.0,10.getContext"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn get_context(canvas: JsValue, kind: &str) -> JsValue {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`get_context` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc = " `context.fillStyle = colour`."]
    #[doc = ""]
    #[doc =
    " A property write, which is a shape of its own: the value is assigned"]
    #[doc = " rather than passed."]
    #[link_section = "rcgjs.ext.2.t-.0,9.fillStyle"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn set_fill_style(context: JsValue, colour: &str) {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`set_fill_style` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc = " `context.fillRect(x, y, width, height)`."]
    #[link_section = "rcgjs.ext.2.s-.0,8.fillRect"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn fill_rect(context: JsValue, x: f64, y: f64, width: f64, height: f64) {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`fill_rect` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc = " `target.addEventListener(kind, handler)`."]
    #[link_section = "rcgjs.ext.2.s-.0,16.addEventListener"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn add_listener(target: JsValue, kind: &str, handler: JsValue) {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`add_listener` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc = " `element.getBoundingClientRect()`."]
    #[link_section = "rcgjs.ext.2.s-.0,21.getBoundingClientRect"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn bounding_rect(element: JsValue) -> JsValue {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`bounding_rect` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc =
    " `rect.left`, which is where the canvas starts across the viewport."]
    #[link_section = "rcgjs.ext.2.g-.0,4.left"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn rect_left(rect: JsValue) -> f64 {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`rect_left` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[doc = " `rect.top`, the same down it."]
    #[link_section = "rcgjs.ext.2.g-.0,3.top"]
    #[inline(never)]
    #[allow(unused_variables)]
    fn rect_top(rect: JsValue) -> f64 {
        {
            ::core::panicking::panic_fmt(format_args!("internal error: entered unreachable code: {0}",
                    format_args!("`rect_top` is a `#[js_extern]` declaration, which only rustc_codegen_js can lower")));
        }
    }
    #[allow(improper_ctypes)]
    unsafe extern "C" {
        /// True the first time `id` asks and false afterwards.
        ///
        /// The frame hole's effect re-runs on every tick, and setting up twice would
        /// register eight listeners and open a second clock.
        fn claim(id: f64)
        -> bool;
        /// The host's board of `len` cells, borrowed to write.
        fn board(id: f64, len: f64)
        -> &'static mut [u8];
        /// The same board, borrowed to read.
        fn board_ro(id: f64, len: f64)
        -> &'static [u8];
        /// Exchanges what two boards hold, which is what makes the board just
        /// written the current one.
        fn board_swap(a: f64, b: f64);
        /// Moves `sig` on by one every `ms`, which is what runs a frame.
        fn clock_start(sig: ::view_abi::Sig<f64>, ms: f64);
        /// The function a pointer event calls, which writes the event's own
        /// `clientX`, `clientY` and `buttons` into the three signals.
        fn pointer_sink(x: ::view_abi::Sig<f64>, y: ::view_abi::Sig<f64>,
        down: ::view_abi::Sig<f64>)
        -> JsValue;
    }
    #[unsafe(no_mangle)]
    #[allow(unused_braces)]
    pub fn __island_sand() -> ::view_abi::Node {
        {
            static __TC_TEMPLATE_0: ::view_abi::TemplateData =
                ::view_abi::TemplateData {
                    abi: ::view_abi::ABI_VERSION,
                    html: "<div class=\"island sand\"><canvas class=\"sand-canvas\" width=\"360\" height=\"240\"></canvas><p class=\"sand-count\">grains <span class=\"sand-grains\"></span> brush <span class=\"sand-brush\"></span></p><div class=\"island-controls\"><button class=\"island-step\" type=\"button\">sand</button><button class=\"island-step\" type=\"button\">wall</button><button class=\"island-step\" type=\"button\">erase</button></div></div>",
                    hydratable: true,
                    is_import_node: false,
                    is_svg: false,
                    holes: &[::view_abi::Hole {
                                    path: "cncn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cncnnn",
                                    kind: ::view_abi::HoleKind::Child,
                                    name: "",
                                    reactive: true,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnnc",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnncn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                },
                                ::view_abi::Hole {
                                    path: "cnncnn",
                                    kind: ::view_abi::HoleKind::DelegatedEvent,
                                    name: "click",
                                    reactive: false,
                                    effect_group: 4294967295u32,
                                }],
                    events: &["click"],
                };
            let tick = ::view_abi::signal(0u32, 0.0);
            let px = ::view_abi::signal(1u32, 0.0);
            let py = ::view_abi::signal(2u32, 0.0);
            let pdown = ::view_abi::signal(3u32, 0.0);
            let brush = ::view_abi::signal(4u32, 0.0);
            let __tc_root_0 = ::view_abi::template(&__TC_TEMPLATE_0);
            ::view_abi::effect(&__tc_root_0, 0u32,
                move ||
                    {
                        let __tc_rows = ::view_abi::list();
                        for grains in frame(tick.get(), tick, px, py, pdown) {
                            ::view_abi::push(&__tc_rows,
                                ::view_abi::content({ grains }));
                        }
                        __tc_rows
                    });
            ::view_abi::effect(&__tc_root_0, 1u32,
                move ||
                    {
                        let __tc_rows = ::view_abi::list();
                        for name in
                            deposit(px.get(), py.get(), pdown.get(), brush.get()) {
                            ::view_abi::push(&__tc_rows, ::view_abi::content({ name }));
                        }
                        __tc_rows
                    });
            ::view_abi::handler(&__tc_root_0, 2u32,
                move |_e: &::view_abi::Event|
                    { pdown.set(0.0); brush.set(0.0) });
            ::view_abi::handler(&__tc_root_0, 3u32,
                move |_e: &::view_abi::Event|
                    { pdown.set(0.0); brush.set(1.0) });
            ::view_abi::handler(&__tc_root_0, 4u32,
                move |_e: &::view_abi::Event|
                    { pdown.set(0.0); brush.set(2.0) });
            __tc_root_0
        }
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub struct SandProps {}
    #[doc(hidden)]
    pub mod __sand_island {
        #[allow(non_camel_case_types)]
        #[allow(dead_code)]
        pub struct AnIslandCannotBeNestedInsideAnotherIsland;
    }
    #[doc(hidden)]
    #[allow(dead_code)]
    pub fn sand(_: __sand_island::AnIslandCannotBeNestedInsideAnotherIsland)
        -> ::view_abi::Node {
        loop {}
    }
}
